import axios from "axios";
import store from "store";
import { setTokenExpired } from "../Redux/authSlice";
import { getCookie, getJwtExpirationDate, removeCookie, setCookie } from "../lib/cookies";
import wlTokenService from "./wlToken.service";

class AuthService {
  constructor() {
    this.TOKEN_KEY = "at";
    this.REFRESH_TOKEN_KEY = "atr";
    this.LEGACY_REFRESH_TOKEN_KEY = "rt";
    this.EXPIRATION_KEY = "at_exp";
    this.CHANNEL_KEY = "oauth_channel";
    this.REFRESH_BUFFER_MS = 60 * 1000;
    this.isRefreshing = false;
    this.failedQueue = [];
    this.refreshTimeoutId = null;
    this._validateChannelInflight = null;
    this._validateChannelInflightKey = null;
    this.initializeRefreshSchedule();
  }

  isValidateChannel500(error) {
    return (
      error?.response?.status === 500 &&
      String(error?.config?.url || "").includes("/jwt/validate-channel")
    );
  }

  extractOAuthBundleFromValidateChannel(payload) {
    if (!payload || typeof payload !== "object") {
      return null;
    }

    const normalizeNested = (block) => {
      if (!block || typeof block !== "object" || typeof block.access_token !== "string") {
        return null;
      }
      return {
        access_token: block.access_token,
        expires_in: block.expires_in,
        refresh_token: block.refresh_token,
        channel: block.channel ?? block.oauth_channel,
      };
    };

    // validate-channel coloca OAuth na raiz; `data` é só o customer — não usar dataLayer como rel principal.
    const root = payload.access_token;
    if (typeof root === "string") {
      return {
        access_token: root,
        expires_in: payload.expires_in,
        refresh_token: payload.refresh_token,
        channel: payload.channel,
      };
    }
    const fromRoot = normalizeNested(root);
    if (fromRoot) {
      return fromRoot;
    }

    const dataLayer =
      payload.data && typeof payload.data === "object" ? payload.data : null;
    if (typeof dataLayer?.access_token === "string") {
      return {
        access_token: dataLayer.access_token,
        expires_in: dataLayer.expires_in ?? payload.expires_in,
        refresh_token: dataLayer.refresh_token ?? payload.refresh_token,
        channel: dataLayer.channel ?? payload.channel,
      };
    }
    return normalizeNested(dataLayer?.access_token);
  }

  saveOAuthFromValidateChannelResponse(payload) {
    const bundle = this.extractOAuthBundleFromValidateChannel(payload);
    if (bundle?.access_token) {
      this.saveAuthData(bundle);
    }
  }

  postValidateChannelDeduped() {
    const token = getCookie("wlt");
    if (!token) {
      return Promise.reject(new Error("Token whitelabel não encontrado"));
    }

    const API_URL_CREDENCIAIS = `${process.env.REACT_APP_API_AUTHORIZATION}/v1`;
    let channel = process.env.REACT_APP_DEFAULT_CHANNEL;
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      channel = payload?.channel ?? channel;
    } catch (err) {
      console.warn("Falha ao decodificar token para obter channel:", err);
    }

    const key = `${channel}|${token}`;
    if (this._validateChannelInflight && this._validateChannelInflightKey === key) {
      return this._validateChannelInflight;
    }

    const pending = axios
      .post(`${API_URL_CREDENCIAIS}/jwt/validate-channel`, { channel, token })
      .then((res) => res.data)
      .finally(() => {
        if (this._validateChannelInflight === pending) {
          this._validateChannelInflight = null;
          this._validateChannelInflightKey = null;
        }
      });

    this._validateChannelInflightKey = key;
    this._validateChannelInflight = pending;

    return pending;
  }

  processQueue(error, token = null) {
    this.failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });

    this.failedQueue = [];
  }

  initializeRefreshSchedule() {
    const expiration = this.getAccessTokenExpirationDate();
    if (expiration) {
      this.scheduleRefresh(expiration);
    }
  }

  parseExpirationDate(expiresIn, token) {
    if (typeof expiresIn === "number" && Number.isFinite(expiresIn)) {
      return new Date(Date.now() + expiresIn * 1000);
    }

    if (typeof expiresIn === "string") {
      const numericValue = Number(expiresIn);
      if (!Number.isNaN(numericValue) && Number.isFinite(numericValue)) {
        return new Date(Date.now() + numericValue * 1000);
      }

      try {
        const [datePart, timePart] = expiresIn.split(" ");
        const [day, month, year] = datePart.split("/");
        const [hours, minutes, seconds] = timePart.split(":");
        const fullYear = year.length === 2 ? `20${year}` : year;

        return new Date(
          parseInt(fullYear, 10),
          parseInt(month, 10) - 1,
          parseInt(day, 10),
          parseInt(hours, 10),
          parseInt(minutes, 10),
          parseInt(seconds || 0, 10)
        );
      } catch (error) {
        console.warn("Falha ao processar expires_in:", error);
      }
    }

    return getJwtExpirationDate(token);
  }

  scheduleRefresh(expirationDate) {
    if (this.refreshTimeoutId) {
      clearTimeout(this.refreshTimeoutId);
      this.refreshTimeoutId = null;
    }

    if (!(expirationDate instanceof Date) || Number.isNaN(expirationDate.getTime())) {
      return;
    }

    const refreshInMs = expirationDate.getTime() - Date.now() - this.REFRESH_BUFFER_MS;
    const timeout = Math.max(0, refreshInMs);

    this.refreshTimeoutId = setTimeout(async () => {
      try {
        await this.ensureValidToken({ proactive: true });
      } catch (error) {
        store.dispatch(setTokenExpired(true));
      }
    }, timeout);
  }

  getAccessTokenExpirationDate() {
    const expirationFromCookie = getCookie(this.EXPIRATION_KEY);
    if (expirationFromCookie) {
      const parsed = new Date(expirationFromCookie);
      if (!Number.isNaN(parsed.getTime())) {
        return parsed;
      }
    }

    const currentToken = this.getAccessToken();
    return getJwtExpirationDate(currentToken);
  }

  isTokenNearExpiration() {
    const expirationDate = this.getAccessTokenExpirationDate();
    if (!expirationDate) {
      return false;
    }

    return expirationDate.getTime() - Date.now() <= this.REFRESH_BUFFER_MS;
  }

  isOAuthAccessJwtExpired() {
    const exp = this.getAccessTokenExpirationDate();
    if (!exp) {
      return false;
    }
    return exp.getTime() <= Date.now();
  }

  saveAuthData(data) {
    if (typeof data === "string") {
      const accessToken = data;
      const accessTokenExpirationDate = this.parseExpirationDate(undefined, accessToken);
      if (accessToken) {
        setCookie(
          this.TOKEN_KEY,
          accessToken,
          accessTokenExpirationDate ? { expiresAt: accessTokenExpirationDate } : {}
        );
        if (accessTokenExpirationDate) {
          setCookie(this.EXPIRATION_KEY, accessTokenExpirationDate.toISOString(), {
            expiresAt: accessTokenExpirationDate,
          });
          this.scheduleRefresh(accessTokenExpirationDate);
        }
      }
      return;
    }

    const accessTokenExpirationDate = this.parseExpirationDate(
      data.expires_in,
      data.access_token
    );

    if (data.access_token) {
      setCookie(
        this.TOKEN_KEY,
        data.access_token,
        accessTokenExpirationDate ? { expiresAt: accessTokenExpirationDate } : {}
      );
      if (accessTokenExpirationDate) {
        setCookie(this.EXPIRATION_KEY, accessTokenExpirationDate.toISOString(), {
          expiresAt: accessTokenExpirationDate,
        });
        this.scheduleRefresh(accessTokenExpirationDate);
      }
    }
    if (data.refresh_token) {
      setCookie(this.REFRESH_TOKEN_KEY, data.refresh_token);
    }
    if (data.channel) {
      setCookie(this.CHANNEL_KEY, data.channel);
    }
  }

  getAccessToken() {
    return getCookie(this.TOKEN_KEY);
  }

  getRefreshToken() {
    return getCookie(this.REFRESH_TOKEN_KEY) || getCookie(this.LEGACY_REFRESH_TOKEN_KEY);
  }

  isTokenExpired() {
    return !this.getAccessToken();
  }

  async getNewToken() {
    try {
      const token = getCookie("wlt");
      if (!token) {
        console.error("Token não encontrado, não é possível obter customer");
        store.dispatch(setTokenExpired(true));
        return null;
      }

      const authData = await this.postValidateChannelDeduped();

      wlTokenService.saveTokens(authData, { accessToken: token });
      const bundle = this.extractOAuthBundleFromValidateChannel(authData);

      if (!bundle?.access_token) {
        throw new Error("Token de autorização não recebido");
      }

      this.saveAuthData(bundle);

      store.dispatch(setTokenExpired(false));

      return bundle.access_token;
    } catch (error) {
      console.error("Erro ao obter novo token:", error);
      if (this.isValidateChannel500(error)) {
        this.clearAuthData();
      }
      store.dispatch(setTokenExpired(true));
      throw error;
    }
  }
  
  async refreshToken() {
    const refreshToken = this.getRefreshToken();
    const clientId = process.env.REACT_APP_API_AUTHORIZATION_CLIENT_ID;
    const clientSecret = process.env.REACT_APP_API_AUTHORIZATION_CLIENT_SECRET;

    if (!refreshToken) {
      store.dispatch(setTokenExpired(true));
      throw new Error("Refresh token não disponível");
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_AUTHORIZATION}/v1/oauth/refresh`,
        {
          client_id: clientId,
          client_secret: clientSecret,
          refresh_token: refreshToken,
        }
      );

      const authData = response?.data?.data;
      
      if (!authData || !authData.access_token) {
        throw new Error("Token de autorização não recebido no refresh");
      }

      this.saveAuthData(authData);
      
      store.dispatch(setTokenExpired(false));

      return authData.access_token;
    } catch (error) {
      console.error("Erro ao renovar token:", error);
      this.clearAuthData();
      store.dispatch(setTokenExpired(true));
      throw error;
    }
  }

  async ensureValidToken(options = {}) {
    const proactive = options?.proactive === true;
    const currentToken = this.getAccessToken();
    const hasRefreshToken = !!this.getRefreshToken();

    let shouldRefresh = !currentToken;
    if (!shouldRefresh && currentToken) {
      if (hasRefreshToken) {
        shouldRefresh = this.isTokenNearExpiration();
      } else if (proactive) {
        shouldRefresh = this.isTokenNearExpiration();
      } else {
        shouldRefresh = this.isOAuthAccessJwtExpired();
      }
    }

    if (shouldRefresh) {
      if (this.isRefreshing) {
        return new Promise((resolve, reject) => {
          this.failedQueue.push({ resolve, reject });
        });
      }

      this.isRefreshing = true;

      try {
        const newToken = hasRefreshToken ? await this.refreshToken() : await this.getNewToken();
        this.processQueue(null, newToken);
        return newToken;
      } catch (error) {
        this.processQueue(error, null);
        throw error;
      } finally {
        this.isRefreshing = false;
      }
    }

    return currentToken;
  }

  clearAuthData() {
    if (this.refreshTimeoutId) {
      clearTimeout(this.refreshTimeoutId);
      this.refreshTimeoutId = null;
    }

    removeCookie(this.TOKEN_KEY);
    removeCookie(this.REFRESH_TOKEN_KEY);
    removeCookie(this.LEGACY_REFRESH_TOKEN_KEY);
    removeCookie(this.EXPIRATION_KEY);
    removeCookie(this.CHANNEL_KEY);
  }
}

export default new AuthService();
